<template>
  <b-table
      :items="tracks"
      responsive
      :fields="tableColumns"
      primary-key="index"
      show-empty
      empty-text="Нет треков"
      class="position-relative"
  >
    <template #cell(row_num)="data">
      {{ data.index + 1 }}
    </template>

    <template #cell(artists)="data">
      <span
          v-for="(artist, artistIndex) in data.item.artists"
          :key="artistIndex"
      >
        <b-link
            v-if="artist.id"
            :to="{ name: 'artist-view', params: { id: artist.id } }"
            class="font-weight-bold"
        >
          {{ artist.name }}
        </b-link>
        <span v-else class="d-inline-block align-top">
          {{ artist.name }}
          <template v-if="artist.name && artist.name !== '.'">
            <b-button
                :to="{ name: 'artists-search', query: { q: artist.name } }"
                variant="info"
                class="btn-icon ml-1"
                size="sm"
            >
              <feather-icon icon="SearchIcon"/>
            </b-button>
            <!-- Популярность во внешних сервисах — чтобы решить, стоит ли добавлять артиста -->
            <artist-external-stats-badge
                :stats="statsByName[artistStatsKey(artist.name)]"
                :loading="Boolean(loadingNames[artistStatsKey(artist.name)])"
                :original-name="artist.name"
            />
          </template>
        </span>
        <span v-if="artistIndex < data.item.artists.length - 1">, </span>
      </span>
    </template>

    <template #cell(duration)="data">
      <span class="text-nowrap">
        {{ data.item.durationMs ? formatDuration(data.item.durationMs / 1000) : '' }}
      </span>
    </template>

    <template #cell(actions)="data">
      <span class="text-nowrap">
        <b-button
            v-if="!data.item.id && data.item.title && data.item.title.trim()"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :variant="(data.item.artists && data.item.artists[0] && data.item.artists[0].id) ? 'success' : 'warning'"
            class="btn-icon ml-1"
            size="sm"
            :disabled="isLoading"
            @click="$emit('add-track', data.item)"
        >
          <feather-icon icon="PlusIcon"/>
        </b-button>
      </span>
    </template>
  </b-table>
</template>

<script>
import { BTable, BLink, BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { computed, watch } from '@vue/composition-api'
import { formatDuration } from '@core/utils/filter'
import ArtistExternalStatsBadge from '@/views/db-music/ArtistExternalStatsBadge.vue'
import useArtistExternalStats, { artistStatsKey } from '@/views/db-music/useArtistExternalStats'

const NAMES_SEPARATOR = '\n'

export default {
  name: 'PlaylistTracksTable',
  components: {
    ArtistExternalStatsBadge,
    BTable,
    BLink,
    BButton,
  },
  directives: {
    Ripple,
  },
  props: {
    tracks: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { statsByName, loadingNames, loadFor } = useArtistExternalStats()

    // Уникальные имена артистов, которых нет в базе (строка — чтобы watch не срабатывал на каждую мутацию)
    const missingArtistNames = computed(() => {
      const namesByKey = {}
      const tracks = Array.isArray(props.tracks) ? props.tracks : []
      tracks.forEach(track => {
        (track.artists || []).forEach(artist => {
          if (artist.id || !artist.name || artist.name === '.') return
          const key = artistStatsKey(artist.name)
          if (key && !namesByKey[key]) namesByKey[key] = artist.name.trim()
        })
      })
      return Object.values(namesByKey).join(NAMES_SEPARATOR)
    })

    // Догружаем статистику автоматически: и при первом показе, и после перезагрузки треков.
    // Новый прогон отменяет предыдущий, поэтому всегда передаём полный список
    watch(missingArtistNames, names => {
      if (names) loadFor(names.split(NAMES_SEPARATOR))
    }, { immediate: true })

    return {
      statsByName,
      loadingNames,
      artistStatsKey,
    }
  },
  data() {
    return {
      tableColumns: [
        { key: 'row_num', label: '№', sortable: false },
        { key: 'artists', label: 'Артисты', sortable: false },
        { key: 'title', label: 'Название', sortable: true },
        { key: 'duration', label: 'Длительность', sortable: false },
        { key: 'actions', label: 'Действия' },
      ],
    }
  },
  methods: {
    formatDuration,
  },
}
</script>
